import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Peer } from './usePresence';
import { Button } from '../../ui';
import { contrastRatio } from '../../ui/contrast';
import { escapeCloses } from '../../ui/escape';

/**
 * Following someone is unmistakable (backlog §5): a border in their colour around the canvas and a banner with Stop;
 * a toast when following ends because the user moved the view; a discreet note of who follows me.
 */
export function FollowBanner({
  followed,
  followers,
  stoppedBy,
  onStop,
  onToastDone,
}: {
  followed: Peer['user'] | null;
  followers: readonly Peer[];
  stoppedBy: 'navigation' | null;
  onStop: () => void;
  onToastDone: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!stoppedBy) return;
    const timer = setTimeout(onToastDone, 3000);
    return () => clearTimeout(timer);
  }, [stoppedBy, onToastDone]);
  // Escape stops following (backlog §19 P16-3.1), unless typing or another layer (a panel, a menu) takes it.
  const following = !!followed;
  useEffect(() => {
    if (!following) return;
    const onKey = (e: KeyboardEvent) => escapeCloses(e, null) && onStop();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [following, onStop]);
  const names = [...new Map(followers.map((p) => [p.user.id, p.user.n])).values()];
  return (
    <>
      {followed && (
        <>
          <div data-follow-border aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 border-[3px]" style={{ borderColor: followed.c }} />
          <div role="status" className="absolute left-1/2 top-16 z-20 flex -translate-x-1/2 items-center gap-3 rounded-full px-4 py-1.5 font-sans text-sm shadow-lg" style={{ background: followed.c, color: contrastRatio('#ffffff', followed.c) >= 4.5 ? '#ffffff' : '#141414' }}>
            <span>{t('presence.following', { name: followed.n })}</span>
            <Button size="sm" onClick={onStop} className="rounded-full">
              {t('presence.stop')}
            </Button>
          </div>
        </>
      )}
      {!followed && stoppedBy && (
        <div role="status" className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-lg bg-surface px-3 py-1.5 font-sans text-sm text-fg shadow-lg">
          {t('presence.stopped')}
        </div>
      )}
      {names.length > 0 && (
        <p className="absolute bottom-20 left-4 z-20 rounded-full bg-surface px-3 py-1 font-sans text-xs text-fg-muted shadow">
          {names.length === 1 ? t('presence.followedBy', { name: names[0] }) : t('presence.followedByMany', { count: names.length })}
        </p>
      )}
    </>
  );
}
